import { auth } from '../firebase';

/**
 * Error tipado a partir de `Problem` (RFC 7807, `application/problem+json`)
 * — backend/services/core/api/openapi.yaml. `status`/`code` son estables y
 * programables: FE-08 los usará para tratar `501`/`403` como estado de UI,
 * no como excepción genérica.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public title: string,
    public detail?: string,
  ) {
    super(`${code}: ${title}`);
    this.name = 'ApiError';
  }
}

/**
 * Renueva el token de Firebase antes de una petición autenticada. Además del
 * cliente REST, lo usa el loader de hls.js para las playlists que sirven
 * desde `/v1/`; los segmentos de GCS no pasan por aquí.
 */
export async function getCurrentFirebaseToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No hay sesión activa — el cliente HTTP necesita un usuario autenticado.');
  }
  // "Renueva el token antes de cada request" — docs/Paquete_Arranque_Frontend.md §7.
  // El SDK cachea y renueva solo cuando hace falta; llamarlo siempre es barato.
  return user.getIdToken();
}

/**
 * Respuesta con el código de estado a la vista. Casi ningún endpoint lo
 * necesita —el cuerpo basta—, pero en `POST /panic` el status ES información
 * de negocio: 201 = emergencia creada, 200 = ya había una activa. Ver
 * `apiPostWithStatus`.
 */
export interface ApiResponse<T> {
  status: number;
  data: T;
}

async function requestWithStatus<T>(path: string, options: RequestInit, token: string | null): Promise<ApiResponse<T>> {
  const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Un `FormData` queda FUERA a propósito: su cabecera la escribe el
      // navegador con el `boundary` del multipart, y fijarla aquí la borraría
      // (FE-12, `apiPostForm`).
      ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  if (res.status === 204) {
    return { status: res.status, data: undefined as T };
  }

  if (!res.ok) {
    const problem = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      problem?.code ?? 'UNKNOWN',
      problem?.title ?? res.statusText,
      problem?.detail,
    );
  }

  return { status: res.status, data: (await res.json()) as T };
}

// Los cuatro helpers de abajo siguen devolviendo solo el cuerpo: el status era
// un detalle de transporte para todos ellos, y sigue siéndolo.
async function request<T>(path: string, options: RequestInit, token: string | null): Promise<T> {
  return (await requestWithStatus<T>(path, options, token)).data;
}

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' }, await getCurrentFirebaseToken());
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }, await getCurrentFirebaseToken());
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }, await getCurrentFirebaseToken());
}

export async function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' }, await getCurrentFirebaseToken());
}

/**
 * BE-45 — para las operaciones donde el código de estado NO es un detalle de
 * transporte sino parte de la respuesta. `POST /vehicles/{id}/panic` es
 * idempotente por vehículo: devuelve 201 con la alerta nueva, o 200 con la que
 * ya estaba activa, y el cuerpo es el mismo schema en los dos casos. Sin el
 * status habría que adivinarlo comparando `occurredAt` con el reloj del
 * cliente, y eso es deducir el contrato del comportamiento observado.
 */
export async function apiPostWithStatus<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  return requestWithStatus<T>(
    path,
    { method: 'POST', body: body ? JSON.stringify(body) : undefined },
    await getCurrentFirebaseToken(),
  );
}

/**
 * FE-12 — subida multipart. El recibo de un gasto NO se sube por URL firmada:
 * va **multipart al core**, porque ADR-009 exige que el SHA-256 lo calcule
 * quien guarda el archivo. Con una URL firmada el core nunca vería los bytes y
 * tendría que creerse un hash que le manda el cliente — y un hash que el emisor
 * elige no prueba nada. La DESCARGA sí es URL firmada.
 *
 * ⚠️ **No fija `Content-Type` a propósito.** Los helpers de arriba ponen
 * `application/json` en cuanto hay body; con un `FormData` eso borraría el
 * `boundary` que el navegador genera y el multipart llegaría ilegible al
 * servidor. El navegador escribe la cabecera completa solo si nadie la toca.
 *
 * ⚠️ El backend lee el Content-Type de la **parte**, no de la petición
 * (`httpapi/expenses.go`, `leerRecibo`): sale del `type` del `File`/`Blob` que
 * se anexa. Un blob sin tipo llega con la cabecera vacía y el core responde
 * 400. Quien construya el FormData tiene que garantizar ese `type`.
 */
export async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  return request<T>(path, { method: 'POST', body: form }, await getCurrentFirebaseToken());
}

/**
 * FE-21 — como `apiPostForm`, pero por `XMLHttpRequest`: `fetch` no expone
 * progreso de subida en los navegadores que soporta esta app, y un adjunto de
 * chat de hasta 50 MiB (video) sin barra de progreso es indistinguible de una
 * subida colgada. Mismo contrato de error que el resto del cliente (`ApiError`
 * a partir del `Problem` RFC 7807).
 */
export function apiPostFormWithProgress<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  return getCurrentFirebaseToken().then(token => new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${import.meta.env.VITE_API_BASE_URL}${path}`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    if (onProgress) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
    }

    xhr.onload = () => {
      if (xhr.status === 204) {
        resolve(undefined as T);
        return;
      }
      let parsed: unknown = null;
      try { parsed = JSON.parse(xhr.responseText); } catch { /* cuerpo vacío o no-JSON */ }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }
      const problem = parsed as { code?: string; title?: string; detail?: string } | null;
      reject(new ApiError(xhr.status, problem?.code ?? 'UNKNOWN', problem?.title ?? xhr.statusText, problem?.detail));
    };
    xhr.onerror = () => reject(new Error('Error de red subiendo el archivo'));
    xhr.send(form);
  }));
}
